import React, { useEffect } from "react";
import * as ReactDOM from "react-dom";
import { CdnFiles } from "./CdnFiles";
import Error from "./components/common/Error";
import NewLoader from "./components/common/NewLoader";
import { ModuleSkeleton } from "./ModuleSkeleton";
import "./skeletonUI/SkeletonUI.css";

// Inline manifest for the NEW flow (used for all modules except WebsiteBuilderModule)
const STATIC_MANIFEST = {
    files: {
        "main.css": "/static/css/main.css",
        "main.js": "/static/js/main.js",
        "main.js.map": "/static/js/main.js.map",
        "index.html": "/index.html",
        "static/css/main.css.map": "/static/css/main.css.map",
        "static/js/main.js.LICENSE.txt": "/static/js/main.js.LICENSE.txt",
    },
    entrypoints: ["static/css/main.css", "static/js/main.js"],
};

function MicroFrontend({ name, host, history }) {
    const scriptId = `micro-frontend-script-${name}`;
    const styleId = `micro-frontend-style-${name}`;

    const targetJs = `${host}${STATIC_MANIFEST.files["main.js"]}`;
    const targetCss = `${host}${STATIC_MANIFEST.files["main.css"]}`;

    const renderMicroFrontend = () => {
        const renderFn = window[`render${name}`];
        if (typeof renderFn === "function") {
            try {
                renderFn(`${name}-container`, history);
            } catch (e) {
                console.log("render module problem", e);
            }
        }
    };

    //
    // ===== OLD FLOW (kept ONLY for WebsiteBuilderModule) =====
    //
    useEffect(() => {
        if (name !== "WebsiteBuilderModule" && name !== 'InvoiceBuilderModule') return;

        const mountError = (err) => {
            console.log(err);
            const mountNode = document.getElementById(`${name}-container`);
            if (mountNode) {
                ReactDOM.render(<Error />, mountNode);
            }
        };

        // If script already exists AND we're on /get-started (legacy special-case), just render.
        if (
            document.getElementById(scriptId) &&
            name !== "DashboardModule" &&
            history?.location?.pathname === "/get-started"
        ) {
            renderMicroFrontend();
            return;
        }

        fetch(`${host}/asset-manifest.json`)
            .then((res) => res.json())
            .then((manifest) => {
                // CSS (prod only)
                if (process.env.REACT_APP_PRODUCTION_MODE === "true") {
                    const link = document.createElement("link");
                    link.id = styleId;
                    link.rel = "stylesheet";
                    link.type = "text/css";
                    link.media = "screen,projection";
                    link.href = `${host}${manifest.files["main.css"]}`;
                    document.head.appendChild(link);
                }

                // Optional CDN files, as before
                CdnFiles(name);

                // JS
                const script = document.createElement("script");
                script.id = scriptId;
                script.crossOrigin = "";
                script.src = `${host}${manifest.files["main.js"]}`;
                script.onload = () => {
                    renderMicroFrontend();
                };
                script.onerror = () => mountError("Failed to load WebsiteBuilder script");
                document.head.appendChild(script);
            })
            .catch(mountError);

        return () => {
            try {
                const unmountFn = window[`unmount${name}`];
                if (typeof unmountFn === "function") {
                    unmountFn(`${name}-container`);
                }
            } catch (_e) {
                // swallow
            }
        };
        // Intentionally no deps to mimic the legacy behavior closely.
    });

    //
    // ===== NEW FLOW (used for all modules EXCEPT WebsiteBuilderModule) =====
    //
    useEffect(() => {
        if (name === "WebsiteBuilderModule" || name === 'InvoiceBuilderModule') return;

        // CSS (prod only)
        if (process.env.REACT_APP_PRODUCTION_MODE === "true") {
            let link = document.getElementById(styleId);
            if (!link) {
                link = document.createElement("link");
                link.id = styleId;
                link.rel = "stylesheet";
                link.type = "text/css";
                link.media = "screen,projection";
                link.href = targetCss;
                document.head.appendChild(link);
            } else if (link.getAttribute("href") !== targetCss) {
                // host changed → swap CSS
                link.setAttribute("href", targetCss);
            }
        }

        // Optional: ensure any CDN files
        CdnFiles(name);

        // JS
        let script = document.getElementById(scriptId);
        const mountError = () => {
            console.log("Failed to load micro-frontend script:", targetJs);
            const mountNode = document.getElementById(`${name}-container`);
            if (mountNode) {
                ReactDOM.render(<Error />, mountNode);
            }
        };

        if (script) {
            // If the existing script points to a different host, replace it
            if (script.getAttribute("src") !== targetJs) {
                script.parentNode?.removeChild(script);
                script = null;
            }
        }

        if (!script) {
            script = document.createElement("script");
            script.id = scriptId;
            script.crossOrigin = "";
            script.src = targetJs;
            script.onload = renderMicroFrontend;
            script.onerror = mountError;
            document.head.appendChild(script);
        } else {
            // Script already present & correct → just render for current route
            renderMicroFrontend();
        }

        // Cleanup on name/host change or unmount
        return () => {
            try {
                const unmountFn = window[`unmount${name}`];
                if (typeof unmountFn === "function") {
                    unmountFn(`${name}-container`);
                }
            } catch (_e) {
                // swallow
            }
        };
    }, [name, host, targetCss, targetJs, styleId, scriptId]);

    // Re-render on route changes (NEW flow only; old WebsiteBuilder keeps original behavior)
    useEffect(() => {
        if (name === "WebsiteBuilderModule" || name === 'InvoiceBuilderModule') return;

        // Immediate re-render on route change
        renderMicroFrontend();

        // Listen for history changes in case parent pushes without rerender
        const unlisten =
            typeof history?.listen === "function"
                ? history.listen(() => {
                    renderMicroFrontend();
                })
                : null;

        return () => {
            if (typeof unlisten === "function") unlisten();
        };
    }, [
        name,
        history,
        history?.location?.pathname,
        history?.location?.search,
        history?.location?.hash,
    ]);

    return (
        <main
            id={`${name}-container`}
            className={`${name}-container-parent-class`}
            style={{ position: "relative" }}
        >
            <div className="parent-loader-div">
                <ModuleSkeleton moduleName={name} />
            </div>
        </main>
    );
}

MicroFrontend.defaultProps = {
    document,
    window,
};

export default MicroFrontend;
